import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Check, Loader2 } from "lucide-react";
import { api } from "../fieldops/lib/api";
import { drw } from "./api";
import CompletionCard from "../fieldops/components/CompletionCard";
import FrsTable from "../fieldops/components/FrsTable";

/** PLAN-COMPLETE §5 — SAVE OPENS THE FILTERED FRS.
 *
 *  Not an interface built for the plan. One room × scope → the REAL completion dialog, the same one
 *  the Install matrix opens when a quantity cell is tapped. Several rooms → the REAL Install
 *  matrix, same columns and layout, filtered to only the tapped rooms and products, each pending
 *  change highlighted in its own cell; tapping a cell opens that same dialog.
 *
 *  Every write — the dialog's Save and Confirm all alike — is `POST /api/completions`, the FRS's
 *  own call. Nothing here writes an FRS record, so rollups, readiness, colour, dates, photos,
 *  history and material consumption cannot drift from the Install view.
 *
 *  §6 — the figures are re-read from the server when this opens (the moment Save was pressed) and
 *  again the moment Confirm all is pressed. If somebody else recorded work on the same record in
 *  between, the new figures are shown and nothing is written until the installer confirms again.
 */
export default function PlanSave({ sheetId, sheetNumber, projectId, author, markupIds,
                                   onClose, onRoomSaved, onAllDone }) {
  const [ctx, setCtx] = useState(null);
  const [left, setLeft] = useState([]);          // room keys still to save
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState(false);     // the FRS moved under us — confirm again
  const [failed, setFailed] = useState([]);

  const keyOf = (r) => `${r.room.key}||${r.scope}`;

  const fetchCtx = useCallback(async (ids) => {
    const { data } = await drw.post(`/sheets/${sheetId}/plan-context`, { markup_ids: ids });
    return data;
  }, [sheetId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await fetchCtx(markupIds);
        if (!alive) return;
        setCtx(data);
        setLeft(data.rooms.map(keyOf));
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not read the FRS for these marks");
        onClose?.();
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [sheetId, JSON.stringify(markupIds)]);

  const rooms = useMemo(() => (ctx?.rooms || []).filter((r) => left.includes(keyOf(r))), [ctx, left]);

  // item_id → units installed once these pending changes are saved. The matrix renders the cell
  // from this instead of the saved completion, so a pending change shows where it will land.
  const pendingItems = useMemo(() => {
    const out = {};
    for (const r of rooms) {
      for (const it of r.items) {
        const qty = Number(it.quantity || 0);
        out[it.id] = Math.max(0, qty - Number(r.preset_remaining?.[it.id] || 0));
      }
    }
    return out;
  }, [rooms]);

  const presetFor = useCallback((cellKey) => {
    const r = rooms.find((x) => keyOf(x) === cellKey);
    return r ? r.preset_remaining || {} : null;
  }, [rooms]);

  const dropRoom = (r) => {
    setLeft((cur) => cur.filter((k) => k !== keyOf(r)));
    onRoomSaved?.(r.markup_ids);
    if (left.filter((k) => k !== keyOf(r)).length === 0) { onAllDone?.(); onClose?.(); }
  };

  const noteFor = () => `Recorded from the drawing ${sheetNumber}`;

  const saveRoom = async (r) => {
    await api.post("/completions", {
      project_id: projectId,
      scope_type: r.scope,
      building: r.room.building || "",
      floor: r.room.floor || "",
      area: r.room.area || "",
      room_number: r.room.room_number || "",
      room_type: r.room.room_type || "",
      mode: "partial",
      remaining_item_ids: Object.keys(r.preset_remaining || {}),
      remaining_units_by_item: r.preset_remaining || {},
      note: noteFor(),
      photos: [],
      author: author || "",
    });
  };

  const confirmAll = async () => {
    setBusy(true);
    setFailed([]);
    try {
      // §6 — re-read at the moment of confirming
      const fresh = await fetchCtx(rooms.flatMap((r) => r.markup_ids));
      const bySig = new Map((fresh.rooms || []).map((r) => [keyOf(r), r]));
      const moved = rooms.filter((r) => (bySig.get(keyOf(r))?.signature || "") !== r.signature);
      if (moved.length && !stale) {
        setCtx({ ...fresh });
        setLeft(fresh.rooms.map(keyOf));
        setStale(true);
        toast.error(`${moved.length} record${moved.length === 1 ? " was" : "s were"} changed by someone else — check the new figures and confirm again`);
        setBusy(false);
        return;
      }
      const target = (fresh.rooms || []).filter((r) => left.includes(keyOf(r)));
      const bad = [];
      const good = [];
      for (const r of target) {
        try { await saveRoom(r); good.push(r); }
        catch (e) { bad.push({ room: r, why: e.response?.data?.detail || "could not be saved" }); }
      }
      if (good.length) {
        onRoomSaved?.(good.flatMap((r) => r.markup_ids));
        toast.success(`${good.length} room${good.length === 1 ? "" : "s"} completed`);
      }
      if (bad.length) {
        // §5 — never show everything as saved when it was not: the rooms that failed stay pending
        // with their marks still selected, and they say why.
        setFailed(bad.map((b) => ({ key: keyOf(b.room), label: `${b.room.room_number} · ${b.room.scope}`, why: String(b.why) })));
        setCtx({ ...fresh });
        setLeft(bad.map((b) => keyOf(b.room)));
        setStale(false);
        toast.error(`${bad.length} room${bad.length === 1 ? "" : "s"} did not save`);
      } else {
        onAllDone?.();
        onClose?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm");
    }
    setBusy(false);
  };

  if (!ctx) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center" data-testid="plan-save-loading">
        <div className="bg-white rounded-xl px-4 py-3 text-sm inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Reading the FRS…
        </div>
      </div>
    );
  }

  const blocked = ctx.blocked || [];
  const single = rooms.length === 1 && !stale && !failed.length;

  if (single) {
    const r = rooms[0];
    return (
      <CompletionCard
        open
        onOpenChange={(o) => { if (!o) onClose?.(); }}
        projectId={projectId}
        scope={r.scope}
        room={r.room}
        items={r.items}
        completion={r.completion}
        presetRemaining={r.preset_remaining || {}}
        author={author}
        origin={sheetNumber}
        onSaved={(saved) => { if (saved !== null) { dropRoom(r); onAllDone?.(); } onClose?.(); }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="plan-save">
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-emerald-50 flex-wrap shrink-0">
        <button type="button" onClick={onClose} className="h-9 px-2 rounded-md border inline-flex items-center gap-1 text-xs font-medium bg-white"
                data-testid="plan-save-cancel"><ArrowLeft className="w-4 h-4" /> Cancel</button>
        <div className="font-semibold text-sm text-emerald-900" data-testid="plan-save-title">
          {rooms.length} room{rooms.length === 1 ? "" : "s"} to complete · from {sheetNumber}
        </div>
        <div className="text-[11px] text-emerald-800 w-full sm:w-auto" data-testid="plan-save-hint">
          Each tapped change is highlighted in its cell. Tap a cell to adjust it or add photos, or confirm them all.
        </div>
        <button type="button" onClick={confirmAll} disabled={busy || !rooms.length}
                className={`ml-auto h-10 px-4 rounded-md text-white text-sm font-bold inline-flex items-center gap-1 disabled:opacity-50 ${stale ? "bg-amber-600" : "bg-emerald-700"}`}
                data-testid="plan-save-confirm">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {stale ? "Confirm the new figures" : `Confirm all ${rooms.length}`}
        </button>
      </div>
      {stale && (
        <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-[12px] text-amber-900 inline-flex items-center gap-2 shrink-0" data-testid="plan-save-stale">
          <AlertTriangle className="w-4 h-4 shrink-0" /> Someone recorded work on one of these records while you were tapping. These are the current figures — nothing has been written yet.
        </div>
      )}
      {failed.length > 0 && (
        <div className="px-3 py-2 bg-rose-50 border-b border-rose-200 text-[12px] text-rose-900 shrink-0" data-testid="plan-save-failed">
          {failed.map((f) => (
            <div key={f.key} data-testid={`plan-save-failed-${f.key}`}>{f.label} did not save — {f.why}. It is still pending and its marks are still selected.</div>
          ))}
        </div>
      )}
      {blocked.length > 0 && (
        <div className="px-3 py-1.5 bg-neutral-50 border-b text-[11px] text-neutral-600 shrink-0" data-testid="plan-save-blocked">
          {blocked.length} tapped mark{blocked.length === 1 ? "" : "s"} could not be completed from the plan (not linked to the FRS) — they stay selected.
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-auto">
        <FrsTable
          projectId={projectId}
          installMode
          installerName={author}
          autoExpand
          focusItemIds={ctx.item_ids}
          focusLabel={`tapped on ${sheetNumber}`}
          pendingItems={pendingItems}
          presetFor={presetFor}
          onPlanSaved={(cellKey) => {
            const r = rooms.find((x) => keyOf(x) === cellKey);
            if (r) dropRoom(r);
          }}
        />
      </div>
    </div>
  );
}
